// frontend/src/pages/Lobby.js

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Modal } from 'react-bootstrap';
import io from 'socket.io-client';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';
import { FaGamepad, FaList, FaHome, FaServer, FaTh } from 'react-icons/fa';
import AnnouncementBoard from '../components/AnnouncementBoard';

// ==== 如果需要自定义背景图 ====
// import lobbyBg from '../assets/lobby-bg.jpg'; // 你自己的图片

const Lobby = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // 分区列表
  const [partitions, setPartitions] = useState([]);
  // 公告列表
  const [announcements, setAnnouncements] = useState([]);
  // Socket
  const [socket, setSocket] = useState(null);

  // “我的设备”弹窗
  const [showMyDevicesModal, setShowMyDevicesModal] = useState(false);
  const [myDevices, setMyDevices] = useState([]);
  const [myDevicesLoading, setMyDevicesLoading] = useState(false);

  // “游戏明细”弹窗
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsPage, setLogsPage] = useState(1);
  const [logsTotalPages, setLogsTotalPages] = useState(1);
  const logsLimit = 10; // 每页10条

  // ========== 新增：点击“类型”跳转玩家账目弹窗 ==========
  const [showLedgerModal, setShowLedgerModal] = useState(false);
  const [ledgerUserId, setLedgerUserId] = useState(null);
  const [ledgerRounds, setLedgerRounds] = useState([]);
  const [ledgerPage, setLedgerPage] = useState(1);
  const [ledgerTotalPages, setLedgerTotalPages] = useState(1);
  const ledgerLimit = 10;
  const [ledgerLoading, setLedgerLoading] = useState(false);

  const handleShowLedgerModal = async (userId, page = 1) => {
    setShowLedgerModal(true);
    setLedgerUserId(userId);
    setLedgerLoading(true);
    try {
      // 使用轮次账目API
      const res = await api.get(`/api/users/${userId}/round-logs?page=${page}&limit=${ledgerLimit}`);
      setLedgerRounds(res.data.logs || []);
      setLedgerPage(res.data.currentPage || 1);
      setLedgerTotalPages(res.data.totalPages || 1);
    } catch (err) {
      alert(err.response?.data?.message || '获取账目失败');
    } finally {
      setLedgerLoading(false);
    }
  };
  const handleCloseLedgerModal = () => setShowLedgerModal(false);
  const goPrevLedgerPage = () => {
    if (ledgerPage <= 1) return;
    handleShowLedgerModal(ledgerUserId, ledgerPage - 1);
  };
  const goNextLedgerPage = () => {
    if (ledgerPage >= ledgerTotalPages) return;
    handleShowLedgerModal(ledgerUserId, ledgerPage + 1);
  };

  // ================== 1) 拉取分区 & 初始化 Socket ==================
  useEffect(() => {
    const fetchPartitions = async () => {
      try {
        const res = await api.get('/api/lobbies');
        setPartitions(res.data.partitions || []);
      } catch (error) {
        console.error('获取分区信息失败:', error);
      }
    };

    const fetchAnnouncements = async () => {
      try {
        const res = await api.get('/api/announcements');
        setAnnouncements(res.data.announcements || []);
      } catch (error) {
        console.error('获取公告信息失败:', error);
      }
    };

    fetchPartitions();
    fetchAnnouncements();

    const token = localStorage.getItem('token');
    // 使用相对 URL，依赖 CRA proxy 转发至后端
    const newSocket = io('/', {
      path: '/socket.io',
      auth: { token },
      transports: ['polling', 'websocket'],
      withCredentials: true,
    });
    newSocket.on('connect', () => {
      console.log('[Socket.io] 连接成功:', newSocket.id);
      newSocket.emit('joinLobby', { userId: user?._id || 'tempUser' });
    });
    newSocket.on('disconnect', () => {
      console.log('[Socket.io] 断开连接');
    });
    setSocket(newSocket);

    // 卸载时 => 断开连接
    return () => {
      newSocket.emit('leaveLobby', { userId: user?._id || 'tempUser' });
      newSocket.disconnect();
    };
  }, [user?._id]);

  // ================== 2) 分区点击 => 进入分区 ==================
  const handleEnterPartition = (partitionId) => {
    navigate(`/lobby/partition/${partitionId}`);
  };

  // ================== 3) “我的设备”功能 ==================
  const handleShowMyDevicesModal = async () => {
    setShowMyDevicesModal(true);
    setMyDevicesLoading(true);
    try {
      const res = await api.get('/api/users/my-devices');
      setMyDevices(res.data.devices || []);
    } catch (err) {
      console.error('获取我的设备失败:', err);
      alert(err.response?.data?.message || '获取我的设备失败');
    } finally {
      setMyDevicesLoading(false);
    }
  };

  // 注意：与 Modal 的 onHide、Footer 的“关闭”按钮对应
  const handleCloseMyDevicesModal = () => {
    setShowMyDevicesModal(false);
  };

  const handleDeviceClick = (deviceId) => {
    setShowMyDevicesModal(false);
    navigate(`/devices/${deviceId}`);
  };

  const handleReleaseDevice = async (device) => {
    if (!window.confirm(`确定要释放【${device.name}】吗？`)) return;
    try {
      // 直接调用后端 releaseDevice，后端自动完成“停止-下分-pending-释放”全流程
      await api.post(`/api/devices/${device._id}/release`);
      alert(`已成功释放设备: ${device.name}`);
      setMyDevices((prev) => prev.filter((d) => d._id !== device._id));
    } catch (err) {
      console.error('释放设备失败:', err);
      alert(err.response?.data?.message || '释放设备失败');
    }
  };

  // ================== 4) “游戏明细”功能 ==================
  const handleShowLogsModal = async (page = 1) => {
    if (!user?._id) {
      alert('尚未登录，无法查看游戏明细');
      return;
    }
    setShowLogsModal(true);
    setLogsLoading(true);
    // 直接显示账目（轮次明细），而不是操作日志
    try {
      const res = await api.get(`/api/users/${user._id}/round-logs?page=${page}&limit=${logsLimit}`);
      setLogs(res.data.logs || []);
      setLogsPage(res.data.currentPage || 1);
      setLogsTotalPages(res.data.totalPages || 1);
    } catch (err) {
      console.error('获取游戏明细失败:', err);
      alert(err.response?.data?.message || '获取游戏明细失败');
    } finally {
      setLogsLoading(false);
    }
  };
  const handleCloseLogsModal = () => {
    setShowLogsModal(false);
  };

  const handlePrevLogs = () => {
    if (logsPage <= 1) return;
    handleShowLogsModal(logsPage - 1);
  };
  const handleNextLogs = () => {
    if (logsPage >= logsTotalPages) return;
    handleShowLogsModal(logsPage + 1);
  };

  // ================== 5) 页面布局渲染 ==================
  return (
    <div className="lobby-page" style={styles.pageWrapper}>
      <Container fluid className="px-4" style={{ paddingBottom: '80px' }}>
        <Row className="justify-content-center">
          {/* 公告栏 */}
          <Col xs={12} lg={8} className="mb-4" style={{ paddingTop: '20px' }}>
            <AnnouncementBoard announcements={announcements} />
          </Col>
          
          {/* 游戏分区 */}
          <Col xs={12}>
            <div className="section-header mb-4">
              <h2 className="section-title" style={styles.pageTitle}>
                <FaTh className="me-3" />
                游戏分区
              </h2>
            </div>
            
            <Row className="g-4">
              {partitions.length === 0 ? (
                <Col xs={12} className="text-center">
                  <div className="empty-state">
                    <FaGamepad size={64} className="text-muted mb-3" />
                    <p className="text-muted">暂无游戏分区</p>
                  </div>
                </Col>
              ) : (
                partitions.map((partition) => (
                  <Col xs={12} sm={6} md={4} lg={3} key={partition._id} className="mb-4">
                    <Card
                      className="simple-partition-card"
                      style={{
                        position: 'relative',
                        height: '220px',
                        border: 'none',
                        borderRadius: '12px',
                        overflow: 'hidden',
                        cursor: 'pointer',
                        background: partition.coverPath
                          ? `url(${api.defaults.baseURL}/uploads/${partition.coverPath})`
                          : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                      }}
                      onClick={() => handleEnterPartition(partition._id)}
                    >
                      {/* 简单遮罩 */}
                      <div className="simple-overlay"></div>
                      
                      {/* 内容层 */}
                      <Card.Body className="simple-card-body">
                        <Card.Title className="simple-card-title">
                          <FaList className="me-2" />
                          {partition.name}
                        </Card.Title>
                        <Card.Text className="simple-card-text">
                          分区游戏
                        </Card.Text>
                        
                        {/* 进入按钮 */}
                        <Button
                          variant="light"
                          size="sm"
                          className="simple-enter-btn"
                          onClick={(e) => { e.stopPropagation(); handleEnterPartition(partition._id); }}
                        >
                          <FaGamepad className="me-2" />
                          进入
                        </Button>
                      </Card.Body>
                    </Card>
                  </Col>
                ))
              )}
            </Row>
          </Col>
        </Row>
      </Container>

      {/* 底部导航栏 */}
      <div style={styles.footerBar}>
        <Container>
          <Row className="h-100">
            {/* 回大厅 */}
            <Col xs={4} style={styles.footerBtnCol}>
              <Button
                variant="primary"
                onClick={() => navigate('/lobby')}
                style={styles.footerBtn}
              >
                <FaHome className="me-1" />
                大厅
              </Button>
            </Col>

            {/* 我的设备 */}
            <Col xs={4} style={styles.footerBtnCol}>
              <Button
                variant="outline-secondary"
                onClick={handleShowMyDevicesModal}
                style={styles.footerBtn}
              >
                <FaServer className="me-1" />
                我的设备
              </Button>
            </Col>

            {/* 游戏明细 */}
            <Col xs={4} style={styles.footerBtnCol}>
              <Button
                variant="outline-secondary"
                onClick={() => handleShowLogsModal(1)}
                style={styles.footerBtn}
              >
                <FaList className="me-1" />
                游戏明细
              </Button>
            </Col>
          </Row>
        </Container>
      </div>

      {/* =========== “我的设备” Modal =========== */}
      <Modal show={showMyDevicesModal} onHide={handleCloseMyDevicesModal}>
        <Modal.Header closeButton>
          <Modal.Title>我占用的设备</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {myDevicesLoading ? (
            <p>加载中...</p >
          ) : myDevices.length === 0 ? (
            <p>当前未占用任何设备</p >
          ) : (
            <ul style={styles.myDevicesList}>
              {myDevices.map((dev) => (
                <li key={dev._id} style={styles.myDeviceItem}>
                  <span
                    style={styles.myDeviceName}
                    onClick={() => handleDeviceClick(dev._id)}
                  >
                    {dev.name} - {dev.description || '无简介'}
                  </span>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => handleReleaseDevice(dev)}
                  >
                    释放
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseMyDevicesModal}>
            关闭
          </Button>
        </Modal.Footer>
      </Modal>

      {/* =========== “游戏明细” Modal =========== */}
      <Modal show={showLogsModal} onHide={handleCloseLogsModal} size="lg" dialogClassName="game-detail-modal-responsive">
        <Modal.Header closeButton>
          <Modal.Title className="game-detail-title">游戏明细</Modal.Title>
        </Modal.Header>
        <Modal.Body className="game-detail-body">
          {logsLoading ? (
            <div className="loading-container"><p>加载中...</p></div>
          ) : logs.length === 0 ? (
            <div className="empty-container"><p>暂无账目记录</p></div>
          ) : (
            <>
              {/* 总输赢统计 */}
              <div className="summary-stats">
                总输赢：<span className="summary-amount">{logs.reduce((sum, r) => sum + (r.netProfit || 0), 0).toFixed(2)}</span>
              </div>
              
              {/* 移动端卡片式显示 */}
              <div className="mobile-card-list d-block d-md-none">
                {logs.map(r => (
                  <div key={r._id} className="game-record-card">
                    <div className="card-header">
                      <span className="round-id">#{r._id.slice(-4)}</span>
                      <span className={`profit ${r.netProfit >= 0 ? 'positive' : 'negative'}`}>
                        {r.netProfit >= 0 ? '+' : ''}{r.netProfit?.toFixed(1)}
                      </span>
                    </div>
                    <div className="card-content">
                      <div className="info-row">
                        <span className="label">设备:</span>
                        <span className="value">{r.deviceName || '未知设备'}</span>
                      </div>
                      <div className="info-row">
                        <span className="label">投注:</span>
                        <span className="value">{r.betTotal?.toFixed(1)}</span>
                        <span className="label">返还:</span>
                        <span className="value">{r.payoutTotal?.toFixed(1)}</span>
                      </div>
                      <div className="info-row">
                        <span className="label">开始:</span>
                        <span className="value small">{new Date(r.startTime).toLocaleString()}</span>
                      </div>
                      {r.endTime && (
                        <div className="info-row">
                          <span className="label">结束:</span>
                          <span className="value small">{new Date(r.endTime).toLocaleString()}</span>
                        </div>
                      )}
                      {r.reason && (
                        <div className="info-row">
                          <span className="label">原因:</span>
                          <span className="value">{r.reason}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              
              {/* 桌面端表格显示 */}
              <div className="desktop-table d-none d-md-block">
                <div className="table-responsive">
                  <table className="table table-bordered table-hover">
                    <thead>
                      <tr>
                        <th>轮次ID</th>
                        <th>设备</th>
                        <th>开始</th>
                        <th>结束</th>
                        <th>投注</th>
                        <th>返还</th>
                        <th>净盈亏</th>
                        <th>原因</th>
                      </tr>
                    </thead>
                    <tbody>
                      {logs.map(r => (
                        <tr key={r._id}>
                          <td>{r._id.slice(-4)}</td>
                          <td>{r.deviceName || '未知设备'}</td>
                          <td>{new Date(r.startTime).toLocaleString()}</td>
                          <td>{r.endTime ? new Date(r.endTime).toLocaleString() : '—'}</td>
                          <td>{r.betTotal?.toFixed(1)}</td>
                          <td>{r.payoutTotal?.toFixed(1)}</td>
                          <td style={{ color: r.netProfit >= 0 ? 'green' : 'red', fontWeight:700 }}>
                            {r.netProfit?.toFixed(1)}
                          </td>
                          <td>{r.reason || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              
              {/* 分页控制 */}
              <div className="pagination-controls">
                <Button 
                  size="sm" 
                  disabled={logsPage<=1} 
                  onClick={handlePrevLogs}
                  className="page-btn"
                >
                  上一页
                </Button>
                <span className="page-info">第 {logsPage}/{logsTotalPages} 页</span>
                <Button 
                  size="sm" 
                  disabled={logsPage>=logsTotalPages} 
                  onClick={handleNextLogs}
                  className="page-btn"
                >
                  下一页
                </Button>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="game-detail-footer">
          <Button variant="secondary" onClick={handleCloseLogsModal} className="close-btn">
            关闭
          </Button>
        </Modal.Footer>
      </Modal>

      {/* =========== 玩家账目 Modal =========== */}
      <Modal show={showLedgerModal} onHide={handleCloseLedgerModal} size="lg" dialogClassName="lobby-modal-responsive">
        <Modal.Header closeButton>
          <Modal.Title>玩家账目</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {ledgerLoading ? (
            <p>加载中...</p>
          ) : ledgerRounds.length === 0 ? (
            <p>暂无账目记录</p>
          ) : (
            <div className="table-responsive">
              <table className="table table-bordered table-hover">
                <thead>
                  <tr>
                    <th>轮次ID</th>
                    <th>设备</th>
                    <th>开始</th>
                    <th>结束</th>
                    <th>投注</th>
                    <th>返还</th>
                    <th>净盈亏</th>
                    <th>原因</th>
                  </tr>
                </thead>
                <tbody>
                  {ledgerRounds.map(r => (
                    <tr key={r._id}>
                      <td>{r._id.slice(-4)}</td>
                      <td>{r.deviceName || '未知设备'}</td>
                      <td>{new Date(r.startTime).toLocaleString()}</td>
                      <td>{r.endTime ? new Date(r.endTime).toLocaleString() : '—'}</td>
                      <td>{r.betTotal?.toFixed(1)}</td>
                      <td>{r.payoutTotal?.toFixed(1)}</td>
                      <td style={{ color: r.netProfit >= 0 ? 'green' : 'red', fontWeight:700 }}>
                        {r.netProfit?.toFixed(1)}
                      </td>
                      <td>{r.reason || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseLedgerModal}>关闭</Button>
        </Modal.Footer>
      </Modal>

      <style>{`
        /* 响应式游戏明细弹窗样式 */
        @media (max-width: 600px) {
          .lobby-modal-responsive .modal-dialog {
            max-width: 100vw !important;
            width: 100vw !important;
            margin: 0 !important;
          }
          .lobby-modal-responsive .modal-content {
            border-radius: 0;
            width: 100vw !important;
            min-width: 0 !important;
            padding: 0;
          }
          .lobby-modal-responsive .modal-body {
            padding: 8px 2px;
            overflow-x: auto;
          }
          .lobby-modal-responsive .table-responsive {
            width: 100%;
            overflow-x: auto;
          }
          .lobby-modal-responsive table {
            font-size: 13px;
            min-width: 540px;
            width: max-content;
            background: #fff;
          }
        }

        /* 游戏明细弹窗的移动端样式 */
        @media (max-width: 768px) {
          .game-detail-modal-responsive .modal-dialog {
            max-width: 95vw;
            margin: 10px auto;
          }
          
          .game-detail-modal-responsive .modal-content {
            border-radius: 12px;
            box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
          }
          
          .game-detail-title {
            font-size: 1.1rem;
            font-weight: 600;
            color: #1a365d;
          }
          
          .game-detail-body {
            padding: 16px;
            max-height: 70vh;
            overflow-y: auto;
          }
          
          .loading-container, .empty-container {
            text-align: center;
            padding: 40px 20px;
            color: #666;
          }
          
          .summary-stats {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            padding: 12px 16px;
            border-radius: 8px;
            margin-bottom: 16px;
            text-align: center;
            font-weight: 600;
            font-size: 1rem;
          }
          
          .summary-amount {
            font-size: 1.2rem;
            font-weight: bold;
            margin-left: 8px;
          }
          
          .mobile-card-list {
            display: flex;
            flex-direction: column;
            gap: 12px;
          }
          
          .game-record-card {
            background: #fff;
            border-radius: 10px;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
            overflow: hidden;
            border: 1px solid #e2e8f0;
          }
          
          .card-header {
            background: linear-gradient(90deg, #f8fafc 0%, #e2e8f0 100%);
            padding: 12px 16px;
            display: flex;
            justify-content: space-between;
            align-items: center;
            border-bottom: 1px solid #e2e8f0;
          }
          
          .round-id {
            font-weight: 600;
            color: #4a5568;
            font-size: 0.9rem;
          }
          
          .profit {
            font-weight: bold;
            font-size: 1rem;
            padding: 4px 8px;
            border-radius: 6px;
          }
          
          .profit.positive {
            color: #22c55e;
            background: rgba(34, 197, 94, 0.1);
          }
          
          .profit.negative {
            color: #ef4444;
            background: rgba(239, 68, 68, 0.1);
          }
          
          .card-content {
            padding: 12px 16px;
          }
          
          .info-row {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 8px;
            flex-wrap: wrap;
            gap: 8px;
          }
          
          .info-row:last-child {
            margin-bottom: 0;
          }
          
          .label {
            font-weight: 500;
            color: #64748b;
            font-size: 0.85rem;
            min-width: 40px;
          }
          
          .value {
            font-weight: 600;
            color: #1e293b;
            font-size: 0.85rem;
            text-align: right;
          }
          
          .value.small {
            font-size: 0.75rem;
            color: #64748b;
          }
          
          .pagination-controls {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-top: 16px;
            padding: 12px 0;
            border-top: 1px solid #e2e8f0;
          }
          
          .page-btn {
            min-width: 70px;
            border-radius: 6px;
            font-weight: 500;
          }
          
          .page-info {
            font-weight: 500;
            color: #64748b;
          }
          
          .game-detail-footer {
            padding: 12px 16px;
            border-top: 1px solid #e2e8f0;
          }
          
          .close-btn {
            width: 100%;
            border-radius: 8px;
            font-weight: 500;
          }
        }
        
        @media (max-width: 480px) {
          .info-row {
            flex-direction: column;
            align-items: flex-start;
            gap: 2px;
          }
          
          .value {
            text-align: left;
          }
          
          .game-detail-title {
            font-size: 1rem;
          }
        }
      `}</style>
    </div>
  );
};

/** 样式定义 */
const styles = {
  pageWrapper: {
    position: 'relative',
    minHeight: '100vh',
    // 如果使用自定义图片:
    // background: `
    //   url(${lobbyBg})
    //   center
    //   / cover
    //   no-repeat
    //   #000
    // `,
    background: '#000',
  },
  pageTitle: {
    textAlign: 'center',
    margin: 0,
    paddingTop: 10,
    fontSize: '1.4rem',
    fontWeight: 700,
    color: '#fff',
  },
  partitionCard: {
    backgroundColor: 'rgba(255,255,255,0.15)',
    backdropFilter: 'blur(6px)',
    border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: '12px',
    boxShadow: '0 3px 12px rgba(0,0,0,0.1)',
    transition: 'all 0.3s',
    cursor: 'pointer',
    height: '220px',
    display: 'flex',
    flexDirection: 'column',
  },
  cardTitle: {
    fontSize: '1.1rem',
    fontWeight: 'bold',
    color: '#fff',
  },
  cardText: {
    fontSize: '0.9rem',
    color: '#eee',
  },
  footerBar: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    height: 60,
    backgroundColor: '#f7f7f7',
    borderTop: '1px solid #ccc',
    zIndex: 1000,
    display: 'flex',
    alignItems: 'center',
  },
  footerBtnCol: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4px',
  },
  footerBtn: {
    width: '100%',
    minWidth: '90px',
    fontWeight: 'bold',
  },
  myDevicesList: {
    listStyle: 'none',
    paddingLeft: 0,
    margin: 0,
  },
  myDeviceItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '6px 0',
    borderBottom: '1px solid #ddd',
  },
  myDeviceName: {
    cursor: 'pointer',
    marginRight: 8,
    color: '#333',
  },
};

export default Lobby;